import * as React from "react";
import {
    Button,
    Form,
    FormAlert,
    Alert,
    Spinner,
    Progress,
    ProgressSize,
    ProgressMeasureLocation
} from "@patternfly/react-core";
import {useState, useEffect, useRef} from "react";
import {updateAd, getPresignedUrl} from "@app/utils/utils";
import toast from "react-hot-toast";
import {
    TimesIcon,
    UploadIcon,
    AutomationIcon,
    EditIcon,
    TagIcon
} from "@patternfly/react-icons";
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { BorderedInput } from '@app/Login/BorderedInput';
import { PURPLE_GRADIENT, colors, radii, fieldLabelStyle, formResetStyle } from '@app/components/designTokens';

export interface IEditAdProps {
    ad: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export const EditAd: React.FunctionComponent<IEditAdProps> = (props) => {
    const [name, setName] = useState('');
    const [tags, setTags] = useState('');
    const [validated, setValidated] = useState('default');
    const [isLoading, setIsLoading] = useState(false);
    const [newFile, setNewFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [aiPrompt, setAiPrompt] = useState('');
    const [isSubmittingPrompt, setIsSubmittingPrompt] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const isAiAd = props.ad && props.ad.adType === 'GENERATE_WITH_AI';

    useEffect(() => {
        if (props.ad) {
            setName(props.ad.name || '');
            const adTags = props.ad.tags || [];
            setTags(Array.isArray(adTags) ? adTags.join(', ') : adTags);
            setValidated('default');
            setNewFile(null);
            setUploadError(null);
            setUploadProgress(0);
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(null);
            setAiPrompt(props.ad.prompt || '');
        }
    }, [props.ad]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp',
                           'video/mp4', 'video/quicktime', 'video/mpeg', 'video/webm'];

    const isVideo = (url: string | undefined): boolean => {
        if (!url) return false;
        const videoExtensions = ['.mp4', '.mov', '.mpeg', '.webm', '.avi'];
        return videoExtensions.some(ext => url.toLowerCase().includes(ext));
    };

    const getMediaType = (file: File): 'IMAGE' | 'VIDEO' => {
        return file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
    };

    const extractS3Uri = (presignedUrl: string): string => {
        try {
            const url = new URL(presignedUrl);
            const hostParts = url.hostname.split('.');
            const bucket = hostParts[0];
            const key = decodeURIComponent(url.pathname.substring(1));
            return `s3://${bucket}/${key}`;
        } catch (e) {
            console.error('Failed to extract S3 URI:', e);
            return '';
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const selectedFile = files[0];
            if (selectedFile.size > MAX_FILE_SIZE) {
                setUploadError('File size must be less than 100MB');
                return;
            }
            if (!ALLOWED_TYPES.includes(selectedFile.type)) {
                setUploadError('Please select a valid image or video file');
                return;
            }
            setNewFile(selectedFile);
            setUploadError(null);
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleRemoveNewFile = () => {
        setNewFile(null);
        setUploadError(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
    };

    const handlePromptChange = (value: string) => {
        const sanitized = value.replace(/<[^>]*>/g, '');
        const limited = sanitized.slice(0, 1000);
        setAiPrompt(limited);
    };

    const uploadWithProgress = (presignedUrl: string, file: File): Promise<void> => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            });
            xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
            xhr.addEventListener('abort', () => reject(new Error('Upload was aborted')));
            xhr.open('PUT', presignedUrl);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.send(file);
        });
    };

    const handleClose = () => {
        setValidated('default');
        props.onClose();
    };

    function validateFields() {
        if (!name) return false;
        return true;
    }

    async function saveAd() {
        if (!validateFields()) {
            setValidated('error');
            return;
        }
        if (isAiAd && !aiPrompt.trim()) {
            setUploadError('Please enter a prompt for AI generation');
            return;
        }
        setIsLoading(true);
        setUploadError(null);
        setUploadProgress(0);
        try {
            if (isAiAd) {
                const updatePayload: any = { "name": name };
                const promptChanged = aiPrompt.trim() && aiPrompt !== props.ad.prompt;
                if (promptChanged) {
                    updatePayload.prompt = aiPrompt.trim();
                    setIsSubmittingPrompt(true);
                }
                await updateAd(props.ad.adId, updatePayload);
                setIsSubmittingPrompt(false);
                if (promptChanged) {
                    toast.success('AI generation started!');
                }
            } else {
                let mediaPayload: { uri: string; type: string; provider: string } | null = null;
                if (newFile) {
                    setIsUploading(true);
                    const presignedData = await getPresignedUrl(
                        props.ad.organizationId,
                        props.ad.adId,
                        newFile.name.trim()
                    );
                    await uploadWithProgress(presignedData['url'], newFile);
                    const s3Uri = extractS3Uri(presignedData['url']);
                    if (s3Uri) {
                        mediaPayload = {
                            uri: s3Uri,
                            type: getMediaType(newFile),
                            provider: 'S3'
                        };
                    }
                    setIsUploading(false);
                }
                const updatePayload: any = { "name": name };
                if (mediaPayload) updatePayload.media = mediaPayload;
                await updateAd(props.ad.adId, updatePayload);
            }
            props.onSave();
            handleClose();
        } catch (error: any) {
            console.error('Failed to save ad:', error);
            if (isUploading || uploadProgress > 0) {
                const errorMessage = error.message || 'Failed to upload media. Please try again.';
                setUploadError(errorMessage);
                toast.error('Media upload failed: ' + errorMessage);
                setIsUploading(false);
            } else if (isSubmittingPrompt) {
                const errorMessage = error.message || 'Failed to submit AI prompt. Please try again.';
                setUploadError(errorMessage);
                toast.error('AI prompt submission failed: ' + errorMessage);
                setIsSubmittingPrompt(false);
            } else {
                setValidated('error');
            }
        } finally {
            setIsLoading(false);
            setIsUploading(false);
            setIsSubmittingPrompt(false);
            setUploadProgress(0);
        }
    }

    const styles = {
        body: { padding: '40px 32px 32px' } as React.CSSProperties,
        header: { textAlign: 'center' as const, marginBottom: '24px' },
        title: { color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' },
        subtitle: { color: colors.subtle, fontSize: '14px', margin: 0, marginBottom: 0 },
        sectionLabel: { fontSize: '14px', fontWeight: 600, color: colors.body, marginBottom: '12px' },
        mediaSection: { marginBottom: '20px' },
        mediaPreviewContainer: {
            position: 'relative' as const,
            width: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#0f172a',
            border: `1px solid ${colors.border}`
        },
        mediaPreview: { width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain' as const, display: 'block' },
        mediaPlaceholder: {
            width: '100%',
            height: '120px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 500
        },
        uploadArea: {
            border: `2px dashed ${colors.border}`,
            borderRadius: '12px',
            padding: '18px',
            textAlign: 'center' as const,
            cursor: 'pointer',
            transition: 'all 0.15s',
            marginTop: '12px',
            background: '#fafafa'
        } as React.CSSProperties,
        uploadIcon: {
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            margin: '0 auto 10px',
            fontSize: '18px'
        },
        uploadText: { color: colors.accent, fontSize: '14px', fontWeight: 600, marginBottom: '4px' },
        uploadHint: { color: colors.placeholder, fontSize: '12px' },
        removeFileButton: {
            position: 'absolute' as const,
            top: '10px',
            right: '10px',
            background: 'rgba(15, 23, 42, 0.65)',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ffffff'
        },
        fileInfo: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            background: '#f5f3ff',
            borderRadius: radii.input,
            marginTop: '12px',
            border: `1px solid #ddd6fe`
        } as React.CSSProperties,
        fileName: { fontSize: '13px', color: colors.title, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, maxWidth: '220px' },
        fileSize: { fontSize: '12px', color: colors.subtle },
        uploadingOverlay: {
            position: 'absolute' as const,
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            padding: '20px'
        },
        uploadingText: { marginTop: '12px', color: colors.accent, fontSize: '14px', fontWeight: 600 },
        progressContainer: { width: '80%', marginTop: '14px' },
        progressLabel: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: colors.muted },
        aiPromptHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' },
        aiPromptIcon: {
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '18px'
        },
        aiPromptLabel: { fontSize: '14px', fontWeight: 600, color: colors.body, margin: 0 },
        aiPromptTextarea: {
            width: '100%',
            minHeight: '120px',
            padding: '14px',
            fontSize: '15px',
            backgroundColor: '#ffffff',
            border: `1px solid ${colors.border}`,
            borderRadius: radii.input,
            outline: 'none',
            resize: 'vertical' as const,
            fontFamily: 'inherit',
            color: colors.body,
            lineHeight: 1.5,
            transition: 'border-color 0.15s, box-shadow 0.15s',
            boxSizing: 'border-box' as const
        },
        charCounter: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '6px' },
        charCount: { fontSize: '12px', color: colors.placeholder },
        charCountWarning: { fontSize: '12px', color: colors.warning },
        aiTips: { marginTop: '14px', padding: '12px', background: '#f5f3ff', borderRadius: radii.input },
        aiTipsTitle: { fontSize: '12px', fontWeight: 600, color: colors.title, marginBottom: '6px' },
        aiTipsList: { margin: 0, paddingLeft: '16px', fontSize: '12px', color: colors.muted, lineHeight: 1.6 },
        actionsContainer: { marginTop: '20px', display: 'flex', flexDirection: 'column' as const, gap: '10px' },
        cancelButton: { color: colors.subtle, fontWeight: 600, fontSize: '14px' },
        infoText: { fontSize: '12px', color: colors.subtle, marginTop: '4px' }
    };

    const isFormValid = name && (!isAiAd || aiPrompt.trim());

    return (
        <AppModal isOpen={props.isOpen} onClose={handleClose} width="md">
            <div style={styles.body}>
                <div style={styles.header}>
                    <LogoBadge size={48} />
                    <h1 style={styles.title}>Edit ad</h1>
                    <p style={styles.subtitle}>Update your ad information</p>
                </div>

                {isAiAd ? (
                    <div style={styles.mediaSection}>
                        <div style={styles.aiPromptHeader}>
                            <div style={styles.aiPromptIcon}>
                                <AutomationIcon />
                            </div>
                            <p style={styles.aiPromptLabel}>AI generation prompt</p>
                        </div>

                        <textarea
                            style={styles.aiPromptTextarea}
                            placeholder="Describe the ad you want to create..."
                            value={aiPrompt}
                            onChange={(e) => handlePromptChange(e.target.value)}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = colors.accent;
                                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.accentRing}`;
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = colors.border;
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        />

                        <div style={styles.charCounter}>
                            <span style={aiPrompt.length > 900 ? styles.charCountWarning : styles.charCount}>
                                {aiPrompt.length}/1000 characters
                            </span>
                        </div>

                        <div style={styles.aiTips}>
                            <p style={styles.aiTipsTitle}>Tips for great results:</p>
                            <ul style={styles.aiTipsList}>
                                <li>Be specific about colors, style, and mood</li>
                                <li>Mention your target audience</li>
                                <li>Include any text you want displayed</li>
                            </ul>
                        </div>

                        {uploadError && (
                            <Alert
                                variant="warning"
                                title={uploadError}
                                aria-live="polite"
                                isInline
                                style={{ marginTop: '12px' }}
                            />
                        )}
                    </div>
                ) : (
                    <div style={styles.mediaSection}>
                        <p style={styles.sectionLabel}>Ad media</p>

                        <div style={styles.mediaPreviewContainer}>
                            {(previewUrl || (props.ad && props.ad.imageUrl)) ? (
                                <>
                                    {(newFile ? newFile.type.startsWith('video/') : isVideo(props.ad && props.ad.imageUrl)) ? (
                                        <video
                                            src={previewUrl || (props.ad && props.ad.imageUrl)}
                                            style={styles.mediaPreview}
                                            controls
                                            muted
                                        />
                                    ) : (
                                        <img
                                            src={previewUrl || (props.ad && props.ad.imageUrl)}
                                            alt="Ad preview"
                                            style={styles.mediaPreview}
                                        />
                                    )}

                                    {newFile && (
                                        <button
                                            style={styles.removeFileButton}
                                            onClick={handleRemoveNewFile}
                                            type="button"
                                            aria-label="Remove selected file"
                                        >
                                            <TimesIcon />
                                        </button>
                                    )}

                                    {isUploading && (
                                        <div style={styles.uploadingOverlay}>
                                            <Spinner size="lg" />
                                            <p style={styles.uploadingText}>Uploading media...</p>
                                            <div style={styles.progressContainer}>
                                                <div style={styles.progressLabel}>
                                                    <span>Progress</span>
                                                    <span>{uploadProgress}%</span>
                                                </div>
                                                <Progress
                                                    value={uploadProgress}
                                                    size={ProgressSize.sm}
                                                    measureLocation={ProgressMeasureLocation.none}
                                                    aria-label="Upload progress"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div style={styles.mediaPlaceholder}>
                                    <span>No media uploaded</span>
                                </div>
                            )}
                        </div>

                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                            accept="image/*,video/*"
                        />
                        <div
                            style={styles.uploadArea}
                            onClick={handleUploadClick}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUploadClick(); }}
                        >
                            <div style={styles.uploadIcon}>
                                <UploadIcon />
                            </div>
                            <p style={styles.uploadText}>
                                {newFile ? 'Change media' : 'Upload new media'}
                            </p>
                            <p style={styles.uploadHint}>Images & videos up to 100MB</p>
                        </div>

                        {newFile && (
                            <div style={styles.fileInfo}>
                                <span style={styles.fileName}>{newFile.name}</span>
                                <span style={styles.fileSize}>
                                    {(newFile.size / (1024 * 1024)).toFixed(2)} MB
                                </span>
                            </div>
                        )}

                        {uploadError && (
                            <Alert
                                variant="warning"
                                title={uploadError}
                                aria-live="polite"
                                isInline
                                style={{ marginTop: '12px' }}
                            />
                        )}
                    </div>
                )}

                <Form style={formResetStyle}>
                    {validated === 'error' && (
                        <FormAlert>
                            <Alert
                                variant="danger"
                                title="Please fill out all required fields."
                                aria-live="polite"
                                isInline
                            />
                        </FormAlert>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="edit-ad-name" style={fieldLabelStyle}>Ad Name</label>
                        <BorderedInput
                            id="edit-ad-name"
                            value={name}
                            onChange={setName}
                            placeholder="Enter ad name"
                            icon={<EditIcon />}
                        />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="edit-ad-tags" style={fieldLabelStyle}>Tags</label>
                        <BorderedInput
                            id="edit-ad-tags"
                            value={tags}
                            onChange={setTags}
                            placeholder="Enter tags (comma separated)"
                            icon={<TagIcon />}
                        />
                        <p style={styles.infoText}>Separate multiple tags with commas</p>
                    </div>

                    <div style={styles.actionsContainer}>
                        <GradientButton
                            onClick={saveAd}
                            isLoading={isLoading}
                            isDisabled={!isFormValid || isLoading}
                            isBlock
                        >
                            {isSubmittingPrompt ? 'Submitting prompt...' : isUploading ? 'Uploading media...' : isLoading ? 'Saving changes...' : isAiAd ? 'Save & generate' : 'Save changes'}
                        </GradientButton>
                        <Button
                            variant="link"
                            onClick={handleClose}
                            style={styles.cancelButton}
                            isBlock
                        >
                            Cancel
                        </Button>
                    </div>
                </Form>
            </div>
        </AppModal>
    );
};
